import { useCallback } from "react";
import type { FileRejection } from "react-dropzone";
import { useDropzone } from "react-dropzone";
import { t, toHumanSize } from "@/i18n-js/instance";
import { useUppyUpload } from "@/react/components/Spaces/CourseSpace/Lessons/Edit/Content/LessonContent/useUppyUpload";
import { useToast } from "@circle-react-shared/uikit/ToastV2";

export const MAX_FILE_SIZE = 4 * 1024 * 1024 * 1024;

export interface InlineAttachment {
  filename?: string;
  signed_id?: string;
  thumbnail_url?: string;
  url?: string;
}

export interface MediaPickerProps {
  onUploadComplete: (file: InlineAttachment) => void;
  onUploadStarted: () => void;
}

export const useMediaPicker = ({
  onUploadStarted,
  onUploadComplete,
}: MediaPickerProps) => {
  const toast = useToast();

  const { uppy, isUploading, progress, isProcessingChecksum } = useUppyUpload({
    onUploadComplete,
    onUploadStarted,
  });

  const handleUpload = (files: File[]) => {
    uppy.getFiles().forEach(file => uppy.removeFile(file.id));
    files.forEach(file => {
      uppy.addFile({
        name: file.name,
        type: file.type,
        data: file,
      });
    });
  };

  const handleFileRejection = useCallback(
    (errors: FileRejection[]) => {
      if (!errors.length) {
        return;
      }
      const error = errors[0];
      if (error.errors[0].code === "file-too-large") {
        toast.error(
          t("builder_ui.prop_type.video_upload.errors.file_too_large", {
            size: toHumanSize(MAX_FILE_SIZE),
          }),
        );
      } else {
        toast.error(t("builder_ui.prop_type.video_upload.errors.invalid_file"));
      }
    },
    [toast],
  );

  const { getRootProps, getInputProps, isDragAccept, isDragReject, open } =
    useDropzone({
      onDropAccepted: handleUpload,
      onDropRejected: handleFileRejection,
      noClick: true,
      disabled: isUploading,
      accept: {
        "video/*": [".mp4", ".mov", ".m4v"],
      },
      maxFiles: 1,
      multiple: false,
      maxSize: MAX_FILE_SIZE,
    });

  const onSelectFile = () => {
    open();
  };

  return {
    getRootProps,
    getInputProps,
    isDragAccept,
    isDragReject,
    open,
    progress,
    isUploading,
    isProcessingChecksum,
    onSelectFile,
  };
};
